import dayjs, {Dayjs} from "dayjs";
import {find, isEmpty} from "lodash";
import {PaymentMethodTypes} from "../../../enums";
import {BaseUseMutationResult} from "../../../requests";
import {Colors, IconCheck} from "../../../ui";
import {IconFailed, IconProgress} from "../../bounce-ui";
import {BundleAccountBreakdown, InstallmentStatus, UserDashboardInstallment} from "../../declarations";

export type PaymentMethodType = "current" | "change" | "remove";

export type PaymentActionType = "update" | "resolve";

export interface IHighlightedInstallment extends UserDashboardInstallment {
  index: number;
}

export interface IInstallmentToPresent {
  minIndex: number;
  maxIndex: number;
}

interface ImapMethodToValue {
  currentMethodType: PaymentMethodTypes | undefined;
  lastDigitsCard: string | undefined;
  showRemoveOption: boolean;
}

export interface IDashboardMenuContent {
  onClose: () => void;
  hasDivider?: boolean;
}

const REVEAL_ALL_UNDER = 10;
const MIDDLE_INSTALLMENT_INDEX = 2;
const MAX_INSTALLMENT_INDEX = 4;

const getMaxDate = (nextPaymentDate: Dayjs, isAch: boolean) => {
  if (isAch) {
    return nextPaymentDate;
  }
  return nextPaymentDate ? nextPaymentDate.subtract(1, "day") : null;
};

const findNextInstallmentIndex = (installments: IHighlightedInstallment[], statuses: InstallmentStatus[]): number => {
  return installments.findIndex((installment) => {
    return statuses.includes(installment.installment_status);
  });
};

const findAllInstallmentIndexes = (
  installments: IHighlightedInstallment[],
  statuses: InstallmentStatus[]
): number[] => {
  return installments.reduce<number[]>((acc, installment, index) => {
    if (statuses.includes(installment.installment_status)) {
      acc.push(index);
    }
    return acc;
  }, []);
};

const findNextUnpaidAfterDate = (
  currDueDate: Dayjs,
  installments: IHighlightedInstallment[]
): IHighlightedInstallment | undefined => {
  return find(
    installments,
    (installment) =>
      ["unpaid", "rescheduled"].includes(installment.installment_status) &&
      dayjs(installment.due_date).isAfter(currDueDate)
  );
};

const findNextPayment = ({
  installments,
  isPlanPendingPayment,
}: {
  installments: IHighlightedInstallment[];
  isPlanPendingPayment: boolean;
}): IHighlightedInstallment | undefined => {
  if (isPlanPendingPayment) return findNextUnpaidAfterDate(dayjs(), installments);

  const lateInstallments = installments.filter((installment) =>
    ["late", "error"].includes(installment.installment_status)
  );
  if (lateInstallments.length > 1) return;
  const latestLateInstallment = lateInstallments[0];
  if (latestLateInstallment) {
    return findNextUnpaidAfterDate(dayjs(latestLateInstallment.due_date), installments);
  }
  const nextUnpaidInstallment = installments.find((installment) => {
    return ["unpaid", "rescheduled"].includes(installment.installment_status);
  });
  if (nextUnpaidInstallment) {
    return findNextUnpaidAfterDate(dayjs(nextUnpaidInstallment.due_date), installments);
  }
};

const findCurrentUnpaid = (installments: IHighlightedInstallment[]): string => {
  const lateInstallments = installments.filter((installment) =>
    ["late", "error"].includes(installment.installment_status)
  );
  if (lateInstallments.length > 0) return "";
  const installment = installments.find((installment) =>
    ["unpaid", "rescheduled"].includes(installment.installment_status)
  );
  return installment ? installment.id : "";
};

const getInstallmentToPresent = (
  revealAll: boolean,
  installments: IHighlightedInstallment[]
): IInstallmentToPresent => {
  const installmentLength = installments.length - 1;
  const showAfterMiddle = MAX_INSTALLMENT_INDEX - MIDDLE_INSTALLMENT_INDEX;
  if (revealAll || installmentLength < REVEAL_ALL_UNDER) {
    return {minIndex: 0, maxIndex: installmentLength};
  } else {
    const nextUnpaidIndex = findNextInstallmentIndex(installments, ["unpaid", "rescheduled"]);
    if (nextUnpaidIndex < 0) return {minIndex: -1, maxIndex: -1};
    if (nextUnpaidIndex < MIDDLE_INSTALLMENT_INDEX) {
      return {minIndex: 0, maxIndex: MAX_INSTALLMENT_INDEX};
    }
    if (nextUnpaidIndex >= installmentLength - showAfterMiddle) {
      return {minIndex: installmentLength - MAX_INSTALLMENT_INDEX, maxIndex: installmentLength};
    }
    return {
      minIndex: nextUnpaidIndex - MIDDLE_INSTALLMENT_INDEX,
      maxIndex: nextUnpaidIndex + showAfterMiddle,
    };
  }
};

const getHighlightedInstallments = (installments: IHighlightedInstallment[]): number[] => {
  const highlightedInstallments: number[] = [];
  const nextLateIndexes = findAllInstallmentIndexes(installments, ["late", "error"]);
  if (!isEmpty(nextLateIndexes)) {
    nextLateIndexes.forEach((nextLateIndex) => {
      highlightedInstallments.push(nextLateIndex + 1);
    });
  } else {
    const nextUnpaidIndex = findNextInstallmentIndex(installments, ["unpaid", "rescheduled"]);
    if (nextUnpaidIndex > 0) {
      highlightedInstallments.push(nextUnpaidIndex);
    }
    if (nextUnpaidIndex >= 0) {
      highlightedInstallments.push(nextUnpaidIndex + 1);
    }
  }
  return highlightedInstallments;
};

const getPaymentCardInstallments = (
  installments: IHighlightedInstallment[],
  action: PaymentActionType,
  hasSpringPermission: boolean
): IHighlightedInstallment[] => {
  const highlightedInstallments: IHighlightedInstallment[] = [];
  if (action === "resolve") {
    const nextLateIndexes = findAllInstallmentIndexes(installments, ["late", "error"]);
    if (!isEmpty(nextLateIndexes)) {
      nextLateIndexes.forEach((nextLateIndex) => {
        highlightedInstallments.push(installments[nextLateIndex]);
      });
    }
  } else if (action === "update") {
    let nextUnpaidIndex;
    if (hasSpringPermission) {
      nextUnpaidIndex = findNextInstallmentIndex(installments, ["late", "error", "unpaid", "rescheduled"]);
    } else {
      nextUnpaidIndex = findNextInstallmentIndex(installments, ["unpaid", "rescheduled"]);
    }
    if (nextUnpaidIndex >= 0) {
      highlightedInstallments.push(installments[nextUnpaidIndex]);
    }
  }
  return highlightedInstallments;
};

const mapStatusToIcon: Record<
  string,
  {Icon: any; actionText: string | null; label: string | null; labelColor: string | null}
> = {
  unpaid: {Icon: null, actionText: "update", label: "(Upcoming)", labelColor: Colors.grey._500},
  paid: {Icon: IconCheck, actionText: null, label: null, labelColor: null},
  partial: {Icon: IconCheck, actionText: null, label: null, labelColor: null},
  late: {Icon: IconFailed, actionText: "resolve", label: "Failed", labelColor: Colors.red._900},
  error: {Icon: IconFailed, actionText: "resolve", label: "Failed", labelColor: Colors.red._900},
  pending: {Icon: IconProgress, actionText: null, label: null, labelColor: null},
  credit: {Icon: IconCheck, actionText: null, label: null, labelColor: Colors.grey._500},
  rescheduled: {Icon: null, actionText: "update", label: "(Upcoming)", labelColor: Colors.grey._500},
};

const getPaymentTypeString = (currentMethodType?: PaymentMethodTypes): string => {
  if (currentMethodType === "ach") {
    return "ACH";
  }
  if (currentMethodType === "apple_pay") {
    return "Apple pay";
  }
  if (currentMethodType === "google_pay") {
    return "Google pay";
  }
  return "Debit card";
};

const mapMethodToValue = ({
  currentMethodType,
  lastDigitsCard,
  showRemoveOption = true,
}: ImapMethodToValue): Record<string, string> => {
  const currentMethodTypeString = getPaymentTypeString(currentMethodType);
  return {
    current: `${currentMethodTypeString} (${lastDigitsCard})`,
    change: "Change payment method",
    ...(showRemoveOption && {remove: "Remove payment method"}),
  };
};

const getDayFromDate = (fullDate: Dayjs): Date => {
  const year = fullDate.toDate().getFullYear();
  const month = String(fullDate.toDate().getMonth() + 1).padStart(2, "0"); // Months are zero-based
  const day = String(fullDate.toDate().getDate()).padStart(2, "0");
  return new Date(`${year}-${month}-${day}`);
};

type UpdateDateVariables = {
  installmentId: string;
  dueDate: Date;
  accountId?: string;
};
type UpdateDateData = string;

type UpdateDateMutation = BaseUseMutationResult<UpdateDateData, UpdateDateVariables>;

export const formatBundleProductLabel = ({product, sub_product: subProduct}: BundleAccountBreakdown): string => {
  if (!subProduct || subProduct === product) return product;
  return `${subProduct} (${product})`;
};

export const getDisplayFourDigits = (userDashboard?: {
  autopay_enrolled?: boolean;
  last_4_digits?: string;
  payment_method_type?: string;
}): boolean =>
  !!(userDashboard?.autopay_enrolled && userDashboard?.last_4_digits && userDashboard?.payment_method_type);

export {
  getMaxDate,
  findNextPayment,
  findAllInstallmentIndexes,
  findCurrentUnpaid,
  getInstallmentToPresent,
  getHighlightedInstallments,
  getPaymentCardInstallments,
  mapStatusToIcon,
  mapMethodToValue,
  getDayFromDate,
};
export type {UpdateDateMutation, UpdateDateData, UpdateDateVariables};
